import { Company } from "@/services/backend/company/company";
import { Address } from "@/services/backend/company/address";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { InputErrorMessages } from "@/shared/components/ui/input-error-messages";
import { Label } from "@/shared/components/ui/label";
import { rtkErrIsValidationError } from "@/shared/app-lib/errors/validation-error";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useState } from "react";

export function CompanyCard({
  company,
  companyAddress,
  onCompanyChange,
  onCompanyAddressChange,
  title = "Prüflabor",
  description = "Daten des ausstellenden Labors",
  fieldErrors = undefined,
  resetFieldError = undefined,
  disabled = false,
}: {
  company: Company;
  companyAddress: Address;
  onCompanyChange: (company: Company) => void;
  onCompanyAddressChange: (address: Address) => void;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
}) {
  const errorMessages = (field: string): string[] => {
    if (!field || field === "") {
      return [];
    }

    return fieldErrors ? (fieldErrors[field] ?? []) : [];
  };

  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            type="text"
            id="name"
            placeholder="Name"
            value={company.name}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("name");
              }
              onCompanyChange({ ...company, name: e.target.value });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("name")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="email">E-Mail</Label>
          <Input
            type="text"
            id="email"
            placeholder="E-Mail"
            value={company.email}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("email");
              }
              onCompanyChange({ ...company, email: e.target.value });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("email")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="phone">Telefon</Label>
          <Input
            type="text"
            id="phone"
            placeholder="Telefon"
            value={company.phone}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("phone");
              }
              onCompanyChange({ ...company, phone: e.target.value });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("phone")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="street">Straße</Label>
          <Input
            type="text"
            id="street"
            placeholder="Straße"
            value={companyAddress.street}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("street");
              }
              onCompanyAddressChange({
                ...companyAddress,
                street: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("street")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="houseNumber">Hausnummer</Label>
          <Input
            type="text"
            id="houseNumber"
            placeholder="Hausnummer"
            value={companyAddress.houseNumber}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("houseNumber");
              }
              onCompanyAddressChange({
                ...companyAddress,
                houseNumber: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("houseNumber")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="postbox">Postfach</Label>
          <Input
            type="text"
            id="postbox"
            placeholder="Postfach"
            value={companyAddress.postbox}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("postbox");
              }
              onCompanyAddressChange({
                ...companyAddress,
                postbox: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("postbox")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="postalCode">Postleitzahl</Label>
          <Input
            type="text"
            id="postalCode"
            placeholder="Postleitzahl"
            value={companyAddress.postalCode}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("postalCode");
              }
              onCompanyAddressChange({
                ...companyAddress,
                postalCode: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("postalCode")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="city">Stadt</Label>
          <Input
            type="text"
            id="city"
            placeholder="Stadt"
            value={companyAddress.city}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("city");
              }
              onCompanyAddressChange({
                ...companyAddress,
                city: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("city")} />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="country">Land</Label>
          <Input
            type="text"
            id="country"
            placeholder="Land"
            value={companyAddress.country}
            onChange={(e) => {
              if (resetFieldError) {
                resetFieldError("country");
              }
              onCompanyAddressChange({
                ...companyAddress,
                country: e.target.value,
              });
            }}
            disabled={disabled}
          />
          <InputErrorMessages errors={errorMessages("country")} />
        </div>
      </CardContent>
    </Card>
  );
}

type Mutation<Req, Res> = Readonly<
  () => [
    (entity: Req) => Promise<{ data: Res } | { error: unknown }> | void,
    {
      isLoading: boolean;
      error: unknown;
      isSuccess: boolean;
      reset: () => void;
    },
  ]
>;

export function CompanyCardWithMutation<
  Req extends { id: string; company: Company; companyAddress: Address },
  Res,
>({
  id,
  company,
  companyAddress,
  mutation,
  title = "Prüflabor",
  description = "Daten des ausstellenden Labors",
  fieldErrors = undefined,
  resetFieldError = undefined,
  disabled = false,
}: {
  id: string;
  company: Company;
  companyAddress: Address;
  mutation: Mutation<Req, Res>;
  title?: string;
  description?: string;
  fieldErrors?: { [key: string]: string[] };
  resetFieldError?: (field: string) => void;
  disabled?: boolean;
}) {
  // @ts-expect-error - I do not understand the error. But it works.
  const [request, setRequest] = useState<Req>({ id, company, companyAddress });
  // @ts-expect-error - I do know how to define an always fitting type for RTK query mutations.
  const [update, { isLoading, error, isSuccess, reset }] = mutation();
  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    update,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  const joinedFieldErrors = {
    ...fieldErrors,
    ...(rtkErrIsValidationError(error) ? error.data.errors : {}),
  } as { [key: string]: string[] };

  return (
    <CompanyCard
      company={request.company}
      companyAddress={request.companyAddress}
      onCompanyChange={(c) => {
        // @ts-expect-error - I do know how to define an always fitting type for RTK query mutations.
        setRequest({ id, company: c, companyAddress: request.companyAddress });
      }}
      onCompanyAddressChange={(a) => {
        // @ts-expect-error - I do know how to define an always fitting type for RTK query mutations.
        setRequest({ id, company: request.company, companyAddress: a });
      }}
      title={title}
      description={description}
      fieldErrors={joinedFieldErrors}
      resetFieldError={resetFieldError}
      disabled={disabled}
    />
  );
}
